<template>
  <div class="wrapper">
    <div v-if="isLoading" class="empty-card-height px-1">
      <div class="progress2 progress-moved">
        <div class="progress-bar2"></div>
      </div>
    </div>
    <div v-else class="customer-detail-card">
      <div class="card__header d-flex justify-content-between">
        <span class="card__header--topic">
          {{ `${contractIdLabel}: ` }}
          <b>{{ contractId.toString().padStart(8, "0") }}</b>
        </span>
        <div v-if="contractId" name="badge-container">
          <b-badge
            pill
            class="badge-padding text-capitalize"
            :variant="badgeVariant"
          >
            {{ badgeStatus }}
          </b-badge>
        </div>
      </div>
      <div class="card__body">
        <div v-if="isShowGenerateSection && hasEditPermission" class="mb-1">
          <div v-if="isShowUsernameAndResetPassword" class="d-flex">
            <span class="text--content"
              >Username: <b>idc{{ Number(contractId) }}</b>
            </span>
            <div
              class="text--link ml-3"
              @click="openModal('modal-confirm-reset-password')"
            >
              Reset Password
            </div>
          </div>
          <div v-else>
            <div
              class="text--link"
              @click="openModal('modal-confirm-generate-user')"
            >
              Generate User
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span v-if="mappedContractData.customer" class="text--content">
            Customer: <b>{{ mappedContractData.customer.customerName }}</b>
          </span>
          <atom-button
            v-if="isShowEditAddressButton"
            buttonId="edit-address-button"
            buttonStyle="border"
            @click="onClickEditAddress"
          >
            <span class="button-text">Edit Address</span>
          </atom-button>
        </div>
        <div class="address-card-container">
          <molecule-address-card
            class="mb-2"
            addressType="MAIN ADDRESS"
            :addressDetail="selectedCustomerMainAddress"
          ></molecule-address-card>
          <b-collapse v-model="isShowAllAddress" id="others-address">
            <molecule-address-card
              class="mb-2"
              addressType="BILLING ADDRESS"
              :addressDetail="selectedCustomerBillingAddress"
            ></molecule-address-card>
            <molecule-address-card
              addressType="MAILING ADDRESS"
              :addressDetail="selectedCustomerMailingAddress"
            ></molecule-address-card>
          </b-collapse>
          <div
            class="d-flex justify-content-center align-items-center custom-hover-effect"
            :class="{ 'pt-2': isShowAllAddress }"
            @click="toggleShowAddress"
          >
            <span v-if="!isShowAllAddress">Show More Addresses</span>
            <span v-else>See less</span>
            <feather-icon
              ref="chevron"
              icon="ChevronDownIcon"
              size="14"
              class="custom-margin-left-8"
            />
          </div>
        </div>
      </div>

      <!-- start modal section -->
      <molecule-modal-dialog
        modalId="modal-generate-username"
        textTitle="Generate User"
        :isButtonLeft="false"
        :isShowXIcon="false"
        :isSlotBody="true"
        :isCloseOnBackDrop="false"
        @clickButtonRight="onClickCloseIconFromModal"
      >
        <template #body>
          <div class="w-80 mt-1">
            <atom-copy-input
              title="Username"
              :showMessage="generatedAccountData.username"
              :copyValue="generatedAccountData.username"
              @onClickCopy="onClickCopy"
            ></atom-copy-input>
          </div>
          <div class="w-80 mt-1">
            <atom-copy-input
              title="Password"
              :showMessage="generatedAccountData.password"
              :copyValue="generatedAccountData.password"
              @onClickCopy="onClickCopy"
            ></atom-copy-input>
          </div>
        </template>
      </molecule-modal-dialog>

      <molecule-modal-dialog
        modalId="modal-confirm-generate-user"
        :isTextCenter="true"
        :textTitle="`Do you want to generate\naccount for “Contract ID: ${contractId}” ?`"
        buttonTextLeft="Confirm"
        buttonTextRight="Cancel"
        @clickButtonLeft="generateUser"
      >
      </molecule-modal-dialog>

      <molecule-modal-dialog
        modalId="modal-confirm-reset-password"
        :isTextCenter="true"
        :textTitle="`Do you want to reset\npassword for “Contract ID: ${contractId}” ?`"
        textSubtitle="This Contract's password will be changed"
        buttonTextLeft="Confirm"
        buttonTextRight="Cancel"
        @clickButtonLeft="resetPassword"
      >
      </molecule-modal-dialog>
      <!-- end modal section -->
    </div>
  </div>
</template>

<script>
import { BBadge, BCollapse } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomCopyInput from "@/components/atoms/AtomCopyInput.vue"
import MoleculeAddressCard from "@/components/molecules/portal/idc/MoleculeAddressCard.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
export default {
  name: "MoleculeCustomerDetail",
  components: {
    BBadge,
    BCollapse,
    AtomButton,
    AtomCopyInput,
    MoleculeAddressCard,
    MoleculeModalDialog,
  },
  props: {
    businessTypeOption: {
      type: Array,
      required: false,
      default: () => [],
    },
    propContractDetail: {
      type: Object,
      required: false,
      default: () => {},
    },
    generatedAccountData: {
      type: Object,
      required: true,
    },
    hasEditPermission: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  mixins: [mixins, mixinsValidator],
  data() {
    return {
      modalId: null,
      isLoading: true,
      isShowAllAddress: false,
      mappedContractData: {},
      selectedCustomerMainAddress: {},
      selectedCustomerBillingAddress: {},
      selectedCustomerMailingAddress: {},
    }
  },
  computed: {
    contractId() {
      return this.$route?.params?.id
    },
    badgeStatus() {
      return this.propContractDetail?.status?.toLowerCase() || "-"
    },
    badgeVariant() {
      switch (this.propContractDetail?.status?.toLowerCase()) {
        case "pending":
          return "light-info"
        case "approved":
          return "light-success"
        case "suspended":
          return "light-warning"
        case "expired":
          return "light-dark"
        case "deleted":
          return "light-danger"
        default:
          return "light-primary"
      }
    },
    isShowGenerateSection() {
      return this.propContractDetail?.status?.toLowerCase() === "approved"
    },
    isShowUsernameAndResetPassword() {
      return (
        this.propContractDetail?.status?.toLowerCase() === "approved" &&
        this.propContractDetail.isUserGenerated
      )
    },
    isShowEditAddressButton() {
      return (
        this.hasEditPermission &&
        this.propContractDetail?.status?.toLowerCase() !== "deleted"
      )
    },
  },
  watch: {
    propContractDetail: {
      deep: true,
      handler() {
        this.mapCustomerDetail()
      },
    },
  },
  methods: {
    mapCustomerDetail() {
      this.isLoading = true
      this.mappedContractData.status = this.propContractDetail.status
      const customerDetail = this.propContractDetail.customer
      this.mappedContractData.customer = {
        customerId: customerDetail.customerId,
        customerName: customerDetail.customerName,
        mainAddress: customerDetail.mainAddress,
        billingAddress: customerDetail.billingAddress,
        mailingAddress: customerDetail.mailingAddress,
      }

      let mainAddress = this.mappedContractData.customer.mainAddress
      mainAddress.businessType = this.getBusinessTypeFromId(
        mainAddress.businessTypeId
      )
      mainAddress.fullAddress = this.getFullAddressDetail(mainAddress)
      this.selectedCustomerMainAddress = mainAddress

      let billingAddress = this.mappedContractData.customer.billingAddress
      billingAddress.businessType = this.getBusinessTypeFromId(
        billingAddress.businessTypeId
      )
      billingAddress.fullAddress = this.getFullAddressDetail(mainAddress)
      this.selectedCustomerBillingAddress = billingAddress

      let mailingAddress = this.mappedContractData.customer.mainAddress
      mailingAddress.businessType = this.getBusinessTypeFromId(
        billingAddress.businessTypeId
      )
      mailingAddress.fullAddress = this.getFullAddressDetail(mainAddress)
      this.selectedCustomerMailingAddress = mailingAddress
      this.isLoading = false
    },

    getBusinessTypeFromId(businessTypeId) {
      return businessTypeId
        ? this.businessTypeOption.find(
            (item) => item.businessTypeId === businessTypeId
          )
        : null
    },

    getFullAddressDetail(addressData) {
      return [
        addressData?.address,
        addressData?.subDistrict,
        addressData?.district,
        addressData?.province,
        addressData?.country === "Thailand"
          ? "ประเทศไทย"
          : addressData?.country,
        addressData?.postalCode,
      ].join(" ")
    },

    resetPassword() {
      this.closeModal()
      const generateAccountData = {
        contractId: this.contractId,
        customerName:
          this.propContractDetail?.customer?.customerName || undefined,
        siteIds: [
          this.propContractDetail?.generalServiceInfo?.siteId || undefined,
        ],
      }
      this.$emit("onGenerateCustomerAccount", generateAccountData)
      this.openModal("modal-generate-username")
    },

    generateUser() {
      this.closeModal()
      const generateAccountData = {
        contractId: this.contractId,
        customerName:
          this.propContractDetail?.customer?.customerName || undefined,
        siteIds: [
          this.propContractDetail?.generalServiceInfo?.siteId || undefined,
        ],
      }
      this.$emit("onGenerateCustomerAccount", generateAccountData)
      this.openModal("modal-generate-username")
    },

    onClickEditAddress() {
      const route = this.$router.resolve({
        path:
          "/internal/web-portal/idc/customer/" +
          this.propContractDetail.customer.customerId,
      })
      window.open(route.href)
    },

    onClickCopy() {
      this.toastSuccess("Copy successfully")
    },

    onClickCloseIconFromModal() {
      this.$router.go()
    },

    // open modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    // close modal
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },

    toggleShowAddress() {
      this.rotateChevron()
      this.isShowAllAddress = !this.isShowAllAddress
    },

    rotateChevron() {
      let rotateUp = this.$refs.chevron.classList.contains("chevron-up")
      let rotateDown = this.$refs.chevron.classList.contains("chevron-down")
      if (!rotateUp && !rotateDown) {
        this.$refs.chevron.classList.add("chevron-up")
      } else if (rotateUp) {
        this.$refs.chevron.classList.remove("chevron-up")
        this.$refs.chevron.classList.add("chevron-down")
      } else {
        this.$refs.chevron.classList.remove("chevron-down")
        this.$refs.chevron.classList.add("chevron-up")
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.customer-detail-card {
  padding: 21px;
}

.card__header {
  margin-bottom: 14px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.card__header--value {
  font-weight: 600;
  font-size: 18px;
}

.text--link {
  color: #7367f0;
  text-decoration: underline;
  cursor: pointer;
}

.badge-padding {
  padding: 6px 8px 6px 8px;
}

.empty-card-height {
  height: 407px;
}

.progress2 {
  border-radius: 30px;
  background-color: #eeedfd;
}

.progress-bar2 {
  height: 3px;
  border-radius: 30px;
  transition: 0.4s linear;
  transition-property: width, background-color;
}

.progress-moved .progress-bar2 {
  animation: progress 2s infinite;
}

@keyframes progress {
  0% {
    width: 0%;
    background: #7367f0;
  }

  100% {
    width: 100%;
    background: #7367f0;
  }
}

.chevron-down {
  transition: transform 0.2s ease-in-out;
  transform: rotate(0deg);
}

.chevron-up {
  transition: transform 0.2s ease-in-out;
  transform: rotate(180deg);
}

.custom-hover-effect {
  color: #b9b9c3;
  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: #7367f0;
  }
}
</style>
